import { useFormik } from 'formik';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Difficulties, Statuses } from '../../domain/Constants';
import { getCourses } from "../../store/courseSlice";
import { createLesson, getLessons, updateLesson } from '../../store/lessonSlice';
import { getPrograms } from '../../store/programSlice';
import Loading from '../common/Loading';
import BreadcrumbNav from '../curriculum/BreadcrumbNav';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Combobox } from '../ui/combobox';
import { Switch } from '../ui/switch';
import { useContentManagement } from '../../hooks/useContentManagement';
import ContentManagementSection from './ContentManagementSection';

const LessonForm = () => {
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const loading = useSelector(state => state.lesson.loading);
    const allLessons = useSelector(state => state.lesson.list);
    const courseList = useSelector(state => state.course.list);
    const programList = useSelector(state => state.program.list);
    const courseOptions = courseList.map((item) => { return { value: `${item.id}`, label: item.name } }) || [];
    const fileRef = useRef();
    const navigate = useNavigate();
    const { lessonId, courseId } = useParams();
    const dispatch = useDispatch();

    // Check if we're editing an existing lesson
    const isEditing = lessonId && lessonId !== 'new';
    const currentLesson = isEditing ? allLessons.find(l => l.id === parseInt(lessonId)) : null;

    useEffect(() => {
        dispatch(getCourses());
        dispatch(getPrograms());
        if (isEditing) {
            dispatch(getLessons());
        }
    }, [dispatch, isEditing]);

    // Handle navigation after successful submission
    useEffect(() => {
        if (isSubmitting && !loading) {
            // If we came from a course's lessons page (URL has courseId)
            if (courseId) {
                navigate(`/curriculum/courses/${courseId}`);
            } 
            // If editing and we know the course from the lesson data
            else if (isEditing && currentLesson && currentLesson.course_id) {
                navigate(`/curriculum/courses/${currentLesson.course_id}`);
            } 
            // Otherwise go back to main curriculum page
            else {
                navigate('/curriculum');
            }
            setIsSubmitting(false);
        }
    }, [isSubmitting, loading, navigate, courseId, currentLesson, isEditing]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            content: currentLesson ? currentLesson.content : '',
            content_ext: currentLesson ? currentLesson.content_ext || [] : [],
            course_id: currentLesson ? `${currentLesson.course_id}` : (courseId ? courseId : ''),
            imageId: currentLesson ? currentLesson.imageId : null,
            name: currentLesson ? currentLesson.name : '',
            difficulty: currentLesson ? currentLesson.difficulty : Difficulties[0].value,
            featured: currentLesson ? currentLesson.featured : false,
            status: currentLesson ? currentLesson.status : Statuses[0].value,
        },
        validate: values => {
            const errors = {};
            if (!values.name || values.name.length === 0) {
                errors.name = 'Required';
            }

            if (!values.difficulty || values.difficulty.length === 0) {
                errors.difficulty = 'Required';
            }

            if (!values.course_id || values.course_id.length === 0) {
                errors.course_id = 'Required';
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onSubmit(values);
            fileRef.current.value = null;
            if (!isEditing) {
                resetForm();
            }
        }
    });

    // Content management hook
    const contentManagement = useContentManagement(formik);

    const handleCancel = (e) => {
        e.preventDefault();
        
        // If we came from a course's lessons page (URL has courseId)
        if (courseId) {
            navigate(`/curriculum/courses/${courseId}`);
        } 
        // If editing and we know the course from the lesson data
        else if (isEditing && currentLesson && currentLesson.course_id) {
            navigate(`/curriculum/courses/${currentLesson.course_id}`);
        } 
        // Otherwise go back to main curriculum page
        else {
            navigate('/curriculum');
        }
    };

    const onChangeFile = (event) => {
        let newFile = event.target.files[0];
        setFile(newFile);
    }

    const onSubmit = (values) => {
        setIsSubmitting(true);

        if (isEditing) {
            dispatch(updateLesson(file, {
                id: currentLesson.id,
                content: values.content,
                content_ext: values.content_ext,
                course_id: parseInt(values.course_id),
                imageId: values.imageId,
                name: values.name,
                difficulty: values.difficulty,
                featured: values.featured,
                status: values.status,
            }));
            return;
        }

        dispatch(createLesson(file, {
            content: values.content,
            content_ext: values.content_ext,
            course_id: parseInt(values.course_id),
            imageId: values.imageId,
            name: values.name,
            difficulty: values.difficulty,
            featured: values.featured,
            status: values.status,
        }));
    }

    if (isEditing && !currentLesson) {
        return <div className="p-6">Loading...</div>;
    }

    const getBreadcrumbItems = () => {
        const items = [{ label: 'Curriculum', href: '/curriculum' }];
        
        // Find course - either from URL params or from current lesson
        let currentCourseId = courseId;
        if (!currentCourseId && currentLesson) {
            currentCourseId = currentLesson.course_id;
        }
        
        if (currentCourseId) {
            const course = courseList.find(c => c.id === parseInt(currentCourseId));
            if (course) {
                // Find the program for this course
                const program = programList.find(p => p.id === course.program_id);
                if (program) {
                    items.push({ 
                        label: program.name, 
                        href: `/curriculum/programs/${program.id}` 
                    });
                }
                items.push({ 
                    label: course.name, 
                    href: `/curriculum/courses/${currentCourseId}` 
                });
            }
        }
        
        if (isEditing && currentLesson) {
            // For editing: Curriculum → Program → Course → Lesson → Edit Lesson
            items.push({ 
                label: currentLesson.name, 
                href: null 
            });
            items.push({ 
                label: 'Edit Lesson', 
                href: null 
            });
        } else {
            // For creating: Curriculum → [Program] → [Course] → Create Lesson
            items.push({ 
                label: 'Create Lesson', 
                href: null 
            });
        }
        
        return items;
    };

    return (
        <div className="space-y-6">
            <BreadcrumbNav items={getBreadcrumbItems()} />
            
            <Card>
                <CardHeader>
                    <CardTitle>
                        {isEditing ? 'Edit Lesson' : 'Create New Lesson'}
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Name</label>
                            <Input
                                type="text"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                placeholder="Enter lesson name..."
                            />
                            {formik.touched.name && formik.errors.name && (
                                <div className="text-sm text-destructive">{formik.errors.name}</div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Difficulty</label>
                                <Combobox
                                    options={Difficulties}
                                    value={formik.values.difficulty}
                                    onChange={selected => formik.setFieldValue("difficulty", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select difficulty"
                                />
                                {formik.touched.difficulty && formik.errors.difficulty && (
                                    <div className="text-sm text-destructive">{formik.errors.difficulty}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Course</label>
                                <Combobox
                                    options={courseOptions}
                                    value={formik.values.course_id}
                                    onChange={selected => formik.setFieldValue("course_id", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select course"
                                    disabled={true}
                                />
                                {formik.touched.course_id && formik.errors.course_id && (
                                    <div className="text-sm text-destructive">{formik.errors.course_id}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Combobox
                                    options={Statuses}
                                    value={formik.values.status}
                                    onChange={selected => formik.setFieldValue("status", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select status"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Content (Legacy)</label>
                            <textarea
                                rows={10}
                                className="flex w-full rounded-md border border-input bg-muted px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                name="content"
                                value={formik.values.content}
                                placeholder="Legacy content field (read-only)"
                                readOnly
                            />
                            <p className="text-xs text-muted-foreground">
                                This field is read-only. Content editing has moved to the new content system.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Image</label>
                                <Input
                                    type="file"
                                    name="image"
                                    ref={fileRef}
                                    onChange={onChangeFile}
                                    accept="image/*"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Featured</label>
                                <div className="flex items-center space-x-2 pt-2">
                                    <Switch
                                        name="featured"
                                        checked={formik.values.featured}
                                        onCheckedChange={(checked) => formik.setFieldValue("featured", checked)}
                                    />
                                    <label className="text-sm text-muted-foreground">
                                        Mark as featured lesson
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Content Management Section */}
                        <ContentManagementSection 
                            formik={formik} 
                            contentManagement={contentManagement} 
                        />

                        <div className="flex items-center justify-between pt-4">
                            <div className="flex items-center space-x-3">
                                <Button
                                    type="submit"
                                    disabled={loading || isSubmitting}
                                >
                                    {isEditing ? "Update" : "Create"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={loading || isSubmitting}
                                >
                                    Cancel
                                </Button>
                            </div>

                            {(loading || isSubmitting) && <Loading />}
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default LessonForm; 